/**
 * A policy checked against a policy manifest (./policyManifest.ts): the errors a machine of that
 * manifest would refuse the policy for, and the keys it would keep but not understand.
 *
 * The validator covers the JSON Schema keywords the manifest's `policy.jsonSchema` uses (zod's
 * draft 2020-12 output): type, enum, const, properties, required, additionalProperties,
 * propertyNames, items, prefixItems, min/maxItems, min/maxLength, pattern, minimum / maximum and
 * their exclusive forms, anyOf / oneOf / allOf, not. Any other keyword is ignored. A console with a
 * full validator (ajv) gets the same verdict for the errors; the unknown-key walk is this file's
 * own rule, and both sides must apply it alike:
 *
 *   An object schema with `properties` and no `additionalProperties` lists what this build knows.
 *   A key of the value not among them is *unknown*: valid (the build keeps it in the file) but
 *   ignored. A schema with `additionalProperties` (a record) has no unknown keys.
 *
 * The workbench host runs it against the policy it applied and reports the unknown keys on the
 * heartbeat (`policy.unknownKeys`), so a console can say "this machine is too old for X". The AIOE
 * mirrors this file byte for byte. No dependency.
 */
const MAX_ISSUES = 200;
function typeOf(value) {
    if (value === null)
        return "null";
    if (Array.isArray(value))
        return "array";
    if (typeof value === "number")
        return Number.isInteger(value) ? "integer" : "number";
    return typeof value;
}
function typeMatches(want, value) {
    const got = typeOf(value);
    if (want === "number")
        return got === "number" || got === "integer";
    return want === got;
}
function equal(a, b) {
    if (a === b)
        return true;
    if (typeof a !== "object" || typeof b !== "object" || a === null || b === null)
        return false;
    if (Array.isArray(a) !== Array.isArray(b))
        return false;
    if (Array.isArray(a))
        return a.length === b.length && a.every((x, i) => equal(x, b[i]));
    const ka = Object.keys(a);
    const kb = Object.keys(b);
    return ka.length === kb.length && ka.every((k) => equal(a[k], b[k]));
}
const join = (path, key) => (path ? `${path}.${key}` : String(key));
/** Validate `value` against `schema`, appending to `errors` and `unknown`. */
function walk(schema, value, path, errors, unknown) {
    if (errors.length >= MAX_ISSUES)
        return;
    if (schema === true || schema === undefined)
        return;
    if (schema === false) {
        errors.push({ path, message: "not allowed here" });
        return;
    }
    const s = schema;
    const fail = (message) => {
        errors.push({ path, message });
    };
    if (s.type !== undefined) {
        const types = Array.isArray(s.type) ? s.type : [s.type];
        if (!types.some((t) => typeMatches(t, value)))
            return fail(`expected ${types.join(" or ")}, got ${typeOf(value)}`);
    }
    if (Array.isArray(s.enum) && !s.enum.some((e) => equal(e, value)))
        return fail(`expected one of ${s.enum.map((e) => JSON.stringify(e)).join(", ")}`);
    if ("const" in s && !equal(s.const, value))
        return fail(`expected ${JSON.stringify(s.const)}`);
    if (typeof value === "string") {
        if (typeof s.minLength === "number" && [...value].length < s.minLength)
            fail(`shorter than ${s.minLength}`);
        if (typeof s.maxLength === "number" && [...value].length > s.maxLength)
            fail(`longer than ${s.maxLength}`);
        if (typeof s.pattern === "string") {
            try {
                if (!new RegExp(s.pattern, "u").test(value))
                    fail(`does not match ${s.pattern}`);
            }
            catch {
                /* a pattern this engine cannot compile is not checked */
            }
        }
    }
    if (typeof value === "number") {
        if (typeof s.minimum === "number" && value < s.minimum)
            fail(`below ${s.minimum}`);
        if (typeof s.maximum === "number" && value > s.maximum)
            fail(`above ${s.maximum}`);
        if (typeof s.exclusiveMinimum === "number" && value <= s.exclusiveMinimum)
            fail(`not above ${s.exclusiveMinimum}`);
        if (typeof s.exclusiveMaximum === "number" && value >= s.exclusiveMaximum)
            fail(`not below ${s.exclusiveMaximum}`);
    }
    if (Array.isArray(value)) {
        if (typeof s.minItems === "number" && value.length < s.minItems)
            fail(`fewer than ${s.minItems} items`);
        if (typeof s.maxItems === "number" && value.length > s.maxItems)
            fail(`more than ${s.maxItems} items`);
        const prefix = Array.isArray(s.prefixItems) ? s.prefixItems : [];
        value.forEach((item, i) => {
            const itemSchema = i < prefix.length ? prefix[i] : s.items;
            if (itemSchema !== undefined)
                walk(itemSchema, item, join(path, i), errors, unknown);
        });
    }
    if (value && typeof value === "object" && !Array.isArray(value)) {
        const obj = value;
        const props = s.properties && typeof s.properties === "object" ? s.properties : undefined;
        for (const req of Array.isArray(s.required) ? s.required : [])
            if (!(req in obj))
                errors.push({ path: join(path, req), message: "required" });
        for (const [k, v] of Object.entries(obj)) {
            if (props && k in props) {
                walk(props[k], v, join(path, k), errors, unknown);
                continue;
            }
            if (s.propertyNames !== undefined)
                walk(s.propertyNames, k, join(path, k), errors, unknown);
            if (s.additionalProperties !== undefined)
                walk(s.additionalProperties, v, join(path, k), errors, unknown);
            else if (props)
                unknown.push(join(path, k));
        }
    }
    if (Array.isArray(s.allOf))
        for (const sub of s.allOf)
            walk(sub, value, path, errors, unknown);
    for (const key of ["anyOf", "oneOf"]) {
        if (!Array.isArray(s[key]))
            continue;
        // The first branch that takes the value decides, and only its unknown keys count.
        let matched = false;
        for (const sub of s[key]) {
            const e = [];
            const u = [];
            walk(sub, value, path, e, u);
            if (!e.length) {
                unknown.push(...u);
                matched = true;
                break;
            }
        }
        if (!matched)
            fail(`matches none of the ${key === "anyOf" ? "alternatives" : "options"}`);
    }
    if (s.not !== undefined) {
        const e = [];
        walk(s.not, value, path, e, []);
        if (!e.length)
            fail("matches a form that is not allowed");
    }
}
/** Check a policy document against a manifest's `policy.jsonSchema`. */
export function checkPolicyAgainstSchema(policy, jsonSchema) {
    const errors = [];
    const unknownKeys = [];
    walk(jsonSchema, policy, "", errors, unknownKeys);
    return { errors: errors.slice(0, MAX_ISSUES), unknownKeys: [...new Set(unknownKeys)].slice(0, MAX_ISSUES) };
}
/** The same, from the manifest itself. */
export function checkPolicyAgainstManifest(policy, manifest) {
    return checkPolicyAgainstSchema(policy, manifest.policy.jsonSchema);
}
//# sourceMappingURL=policyManifestCheck.js.map